import {
  getAuth,
  getMultiFactorResolver,
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
} from 'firebase/auth';
import { useContext, useState } from 'react';
import AuthContext from '@/contexts/auth';
import firebase from '@/plugins/firebaseConfig';

const useMFA = () => {
  const [verificationId, setVerificationId] = useState<string>('');
  const { loginError, auth, enrolledFactors } = useContext(AuthContext);

  const [multiFactorResolver, setMultiFactorResolver] = useState<any>(undefined);

  /**
   * Send MFA SMS for MFA enrollment
   * @param phoneNumber
   * @returns
   */
  const enrollSendSMS = async (phoneNumber: string) => {
    try {
      const _auth = getAuth(firebase);
      const _recaptchaVerifier = new RecaptchaVerifier('recaptcha-enroll', {}, _auth);
      if (_recaptchaVerifier) {
        const auth = getAuth(firebase);
        const _user = auth.currentUser;
        if (_user)
          return multiFactor(_user)
            .getSession()
            .then(async (multiFactorSession) => {
              // Specify the phone number and pass the MFA session.
              const phoneInfoOptions = {
                phoneNumber: phoneNumber,
                session: multiFactorSession,
              };

              const phoneAuthProvider = new PhoneAuthProvider(auth);

              // Send SMS verification code.
              const _verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, _recaptchaVerifier);
              _recaptchaVerifier.clear();
              setVerificationId(_verificationId);
              return verificationId;
            });
      }
    } catch (error) {
      console.log('🚀 ~ file: useMFA.ts:51 ~ enrollSendSMS ~ error:', error);
      throw error;
    }
  };

  const unEnroll = () => {
    const auth = getAuth();

    return Promise.all(
      enrolledFactors.map(async (enrolledFactor) => {
        if (auth.currentUser) await multiFactor(auth.currentUser).unenroll(enrolledFactor);
        return;
      }),
    );
  };

  /**
   * Enroll MFA Authentication
   * @param verificationCode
   */
  const verifyEnrollMFA = async (verificationCode: string) => {
    if (verificationId !== '' && verificationCode && auth) {
      // Remove all MultiFactorUser registed
      await unEnroll();

      const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
      if (auth.currentUser)
        // Complete enrollment.
        await multiFactor(auth.currentUser)
          .enroll(multiFactorAssertion, 'MFA USER')
          .catch(function (error) {
            throw error;
          });
      setVerificationId('');
    }
  };

  /**
   * Send SMS verification code
   * @returns
   */
  const loginSendSMS = async () => {
    if (loginError && loginError.code == 'auth/multi-factor-auth-required') {
      const recaptchaVerifier = new RecaptchaVerifier('recaptcha-login', {}, auth);

      const resolver = getMultiFactorResolver(auth, loginError);
      setMultiFactorResolver(resolver);

      if (resolver.hints[0].factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
        const phoneInfoOptions = {
          multiFactorHint: resolver.hints[0],
          session: resolver.session,
        };
        const phoneAuthProvider = new PhoneAuthProvider(auth);
        try {
          // Send SMS verification code
          const _verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);
          setVerificationId(_verificationId);
          recaptchaVerifier.clear();
          return verificationId;
        } catch (error) {
          console.log('🚀 ~ file: useMFA.ts:115 ~ loginSendSMS ~ error:', error);
        }
      }
    }
  };

  /**
   *  Verify sign using MFA code
   * @param verificationCode
   */
  const verifyLoginMFA = async (verificationCode: string) => {
    if (verificationId && multiFactorResolver && verificationCode) {
      const credential = PhoneAuthProvider.credential(verificationId, verificationCode);
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(credential);
      // Complete sign-in.
      await multiFactorResolver.resolveSignIn(multiFactorAssertion);
    }
  };

  return { enrollSendSMS, verifyEnrollMFA, loginSendSMS, verifyLoginMFA, unEnroll };
};

export default useMFA;
